import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import tokens from '@verifime/design-tokens';
import { validationRules } from '@verifime/utils';
import { useState } from 'react';
import { ColoredIcon } from '../ColoredIconAndChip';
import {
  TFormFieldsAndRules as TFormFieldsAndRules_New,
  getFieldsRules as getFieldsRules_New,
} from '../Form/FormGenerator/common/utils';
import { RenderType } from '../Form/FormGenerator/utils';
import GenerateFormFields_New from '../Form/FormGenerator/v2/GenerateFormFields';
import { useCustomForm } from '../Form/useCustomForm';
import { OrganisationUser } from './type';

const formFieldsAndRules: TFormFieldsAndRules_New = {
  row1: [
    {
      fieldName: 'email',
      label: 'Email',
      renderType: RenderType.Text,
      rules: validationRules.REQUIRED_EMAIL_RULES,
    },
  ],
};

type TFormData<T extends OrganisationUser> = Pick<T, 'email'>;

interface AddOrganisationUserProps<T extends OrganisationUser> {
  onAddUser: (fromData: TFormData<T>) => Promise<void>;
}

export default function AddOrganisationUser<T extends OrganisationUser>({
  onAddUser,
}: Readonly<AddOrganisationUserProps<T>>) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useCustomForm<TFormData<T>>({
    schema: getFieldsRules_New(formFieldsAndRules),
  });

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const onFormSubmit = async (formData: TFormData<T>) => {
    setIsSubmitting(true);
    try {
      await onAddUser(formData);
      handleClose();
    } catch (error) {
      console.error('Error submitting user:', error);
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <Button
        variant="text"
        startIcon={<ColoredIcon iconName="addRounded" />}
        onClick={handleOpen}
        data-action="open-dialog"
      >
        Add New
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        data-component="add-organisation-user-dialog"
      >
        <DialogTitle>Add Organisation User</DialogTitle>

        <DialogContent sx={{ pt: tokens.spacing2xs }}>
          <GenerateFormFields_New
            formFieldsAndRules={formFieldsAndRules as TFormFieldsAndRules_New<keyof TFormData<T>>}
            control={control}
            errors={errors}
          />
        </DialogContent>

        <DialogActions sx={{ px: tokens.spacing2xs, pb: tokens.spacingXs }}>
          <Button onClick={handleClose} color="error" data-action="cancel">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            onClick={handleSubmit(onFormSubmit)}
            data-action="submit"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
